import axios from "axios";
import { z } from "zod";
import { validateAddress, validateQuoteResponse } from "../entities/quote";
import { addressSuggestionValidator, conditionValidator, medicationValidator } from "../entities/suggestion";
import { getSessionId } from "@/utils";
import { handleApiResponse } from "@/utils/api";

type config = {
    apiURL: string
};

type addressConfig = {
    getAddressBaseURL: string,
    getAddressKey: string,
};

export const lookupConditions = async ({ apiURL }: config, q: string) => {
    const response = await axios.get(
        `${apiURL}/v1/annuities/conditions?q=${encodeURIComponent(q)}&sessionId=${getSessionId()}`,
        {
            validateStatus: () => true,
        },
    );

    const successSchema = z.object({
        response: z.object({
            conditions: z.array(conditionValidator),
        }),
    });

    type SuccessResponse = z.infer<typeof successSchema>;
    const result = handleApiResponse<SuccessResponse>(response.data, successSchema, "lookupConditions");
    return result.response.conditions;
};

export const lookupMedications = async ({ apiURL }: config, searchStr: string) => {
    const response = await axios.post(`${apiURL}/v1/annuities/medications`,
        {
            medicalValue: searchStr,
        },
        {
            validateStatus: () => true,
        },
    );

    const successSchema = z.object({
        response: z.object({
            medications: z.array(medicationValidator),
        }),
    });

    type SuccessResponse = z.infer<typeof successSchema>;
    const result = handleApiResponse<SuccessResponse>(response.data, successSchema, "lookupMedications");
    return result.response.medications;
};

export const getPollQuote = async ({ apiURL }: config, pathname: string, token: string) => {
    const res = await axios.get(`${apiURL}${pathname}`, {
        headers: {
            authorization: token,
        },
        validateStatus: () => true,
    });

    const response = validateQuoteResponse.safeParse(res.data);
    if (response.success) {
        return response.data;
    }

    const errors = z.object({
        errors: z.array(z.object({
            message: z.string(),
        })).min(1),
    }).safeParse(res.data);

    if (errors.success) {
        return errors.data;
    } else {
        return response.error;
    }
};

export const fetchAddressSuggestions = async ({
    getAddressBaseURL, getAddressKey,
}: addressConfig, searchStr: string) => {
    const url = new URL(getAddressBaseURL);
    url.pathname = "/address-lookup/autocomplete";
    const res = await axios.post(
        url.toString(),
        {
            address: searchStr,
        },
        {
            headers: {
                authorization: getAddressKey,
            },
        },
    );

    return z.object({
        suggestions: z.array(addressSuggestionValidator),
    }).parse(res.data).suggestions;
};

export const fetchAddress = async ({
    getAddressBaseURL, getAddressKey,
}: addressConfig, addressId: string) => {
    const url = new URL(
        `address-lookup/address/${addressId}`,
        getAddressBaseURL,
    );

    const res = await axios.get(url.toString(), {
        headers: {
            authorization: getAddressKey,
        },
    });

    return validateAddress.parse(res.data);
};

export const getQuoteUrlFromFTrackingToken = async (apiUrl: string, trackingToken: string) => {
    const url = new URL(apiUrl);
    url.pathname = `/v1/myquotes/cf/${trackingToken}`;
    const res = await axios.get(url.toString(), { maxRedirects: 0 });
    return z.object({
        response: z.object({
            quoteNumber: z.string(),
            token: z.string(),
        }),
    }).safeParse(res.data);
};
